import OrdersService from '@/api/services/orders.service';
import type { IGetAllOrdersFilters } from '@/api/services/orders.service';
import { PermissionType } from '@/enums/PermissionType';
import { OrderStatus, OrderType } from '@/enums/OrderEnums';
import {
  HostToolError,
  type ICartadigitalAdminHostToolDeps,
  type IGpthelpTool,
} from '../tool-types';
import {
  getLimitArg,
  getOptionalBoolean,
  getOptionalNumber,
  getOptionalString,
  getOptionalStringArray,
  getPageArg,
  normalizeInstantRange,
  RANGE_END_DESCRIPTION,
} from '../tool-args';
import { runToolApiCall } from '../tool-http';
import { ensureAnyPermissionOrThrow } from '../tool-scope';
import {
  sanitizeCajaMovement,
  sanitizeOrderDetail,
  sanitizeOrderRow,
  sanitizeOrderRowWithItems,
} from '../sanitize';

const ordersService = new OrdersService();

const ORDER_STATUSES: string[] = Object.values(OrderStatus);
const ORDER_TYPES: string[] = Object.values(OrderType);

export const ensureOrdersHistoryAccess = (
  deps: ICartadigitalAdminHostToolDeps,
) =>
  ensureAnyPermissionOrThrow({
    deps,
    permissions: [PermissionType.OrdersHistoric, PermissionType.OrdersManage],
    message: 'No tiene permisos para ver el historial de pedidos',
  });

export const ordersTools: IGpthelpTool[] = [
  {
    definition: {
      name: 'orders_find',
      description:
        "Search the order history (the admin history page). Free-text search matches order id, humanId, phone, address, customer name, item names and table name. Excludes WhatsApp-channel and unpaid online orders, same as the UI. When dateFrom is absent the server applies a 90-day fallback lower bound. For what was ordered across many orders use includeItems; use orders_get only for the full detail of ONE order.",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          search: {
            type: 'string',
            description:
              'Free text: id/humanId/phone/address digits, names, items, table.',
          },
          status: {
            type: 'array',
            items: {
              type: 'string',
              enum: [
                'received',
                'progress',
                'readyta',
                'delivering',
                'finished',
                'declined',
                'canceled',
              ],
            },
            description:
              'Filter by status. Pending/live orders = received/progress/readyta/delivering.',
          },
          types: {
            type: 'array',
            items: { type: 'string', enum: ['seat', 'away', 'delivery'] },
          },
          settledOnly: {
            type: 'boolean',
            default: false,
            description: 'Only fully settled (non-pending) orders.',
          },
          minTotal: { type: 'number' },
          maxTotal: { type: 'number' },
          dateFrom: {
            type: 'string',
            description:
              'Start date (yyyy-MM-dd, branch-local) or ISO instant.',
          },
          dateTo: { type: 'string', description: RANGE_END_DESCRIPTION },
          includeItems: {
            type: 'boolean',
            default: false,
            description:
              "Include each order's item lines (orderItems: index, itemId, name, amount, price, total, comment, options[], canceledAt only on canceled lines). Use this for questions about what was ordered instead of calling orders_get per order. With includeItems the page size is capped at 25.",
          },
          page: { type: 'number', minimum: 1, default: 1 },
          limit: { type: 'number', minimum: 1, maximum: 50, default: 20 },
        },
      },
    },
    policy: 'read',
    readsDinerText: true,
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewOrdersHistory,
    async execute(args, { deps, scope }) {
      ensureOrdersHistoryAccess(deps);

      const search = getOptionalString(args, 'search');
      const status = getOptionalStringArray(args, 'status');
      const types = getOptionalStringArray(args, 'types');
      const settledOnly = getOptionalBoolean(args, 'settledOnly') ?? false;
      const includeItems = getOptionalBoolean(args, 'includeItems') ?? false;
      const minTotal = getOptionalNumber(args, 'minTotal');
      const maxTotal = getOptionalNumber(args, 'maxTotal');
      const page = getPageArg(args);
      // A 50-row page with item lines lands near the tool-result size guard.
      const limit = includeItems
        ? getLimitArg(args, { def: 20, max: 25 })
        : getLimitArg(args, { def: 20, max: 50 });

      const invalidStatus = (status ?? []).filter(
        (s) => !ORDER_STATUSES.includes(s),
      );
      if (invalidStatus.length) {
        throw new HostToolError('INVALID_ARGS', 'Invalid "status"', {
          invalid: invalidStatus,
          allowed: ORDER_STATUSES,
        });
      }
      const invalidTypes = (types ?? []).filter((t) => !ORDER_TYPES.includes(t));
      if (invalidTypes.length) {
        throw new HostToolError('INVALID_ARGS', 'Invalid "types"', {
          invalid: invalidTypes,
          allowed: ORDER_TYPES,
        });
      }

      const { from, to } = normalizeInstantRange({
        obj: args,
        fromKey: 'dateFrom',
        toKey: 'dateTo',
        timeZone: scope.timeZone,
      });

      const filters: IGetAllOrdersFilters = {};
      if (settledOnly) filters.pending = 0;
      if (search) filters.search = search;
      if (minTotal !== null) filters.minTotal = minTotal;
      if (maxTotal !== null) filters.maxTotal = maxTotal;
      if (from) filters.dateFrom = from.toISOString();
      if (to) filters.dateTo = to.toISOString();
      if (includeItems) filters.includeItems = true;

      const data = await runToolApiCall(() =>
        ordersService.getAll(
          scope.restaurantId,
          scope.branchId,
          status?.length ? status : undefined,
          undefined,
          types?.length ? types : undefined,
          { page, itemsPerPage: limit },
          filters,
        ),
      );

      return {
        total: data.total,
        page,
        results: (data.items ?? []).map(
          includeItems ? sanitizeOrderRowWithItems : sanitizeOrderRow
        ),
      };
    },
  },
  {
    definition: {
      name: 'orders_get',
      description:
        'Full detail of ONE order: items with options/comments/canceled lines, customer contact data, discounts, tips, decoded payments split, invoice status. Set includeMovements to also get the caja money movements tied to the order (payments, refunds).',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          orderId: { type: 'number' },
          includeMovements: {
            type: 'boolean',
            default: false,
            description: 'Also fetch the caja movements of this order.',
          },
        },
        required: ['orderId'],
      },
    },
    policy: 'read',
    readsDinerText: true,
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewOrdersHistory,
    async execute(args, { deps, scope }) {
      ensureOrdersHistoryAccess(deps);

      const orderIdRaw = getOptionalNumber(args, 'orderId');
      if (orderIdRaw === null || orderIdRaw <= 0) {
        throw new HostToolError('INVALID_ARGS', 'Missing/invalid "orderId"', {
          orderId: args.orderId,
        });
      }
      const orderId = Math.floor(orderIdRaw);
      const includeMovements = getOptionalBoolean(args, 'includeMovements') ?? false;

      const [order, movements] = await runToolApiCall(() =>
        Promise.all([
          OrdersService.getById(orderId),
          includeMovements
            ? OrdersService.getCajaMovements(orderId)
            : Promise.resolve(null),
        ]),
      );

      // Client tenant fence in addition to the server check.
      if (!order || order.restaurantId !== scope.restaurantId) {
        throw new HostToolError('NOT_FOUND', 'Pedido no encontrado', { orderId });
      }

      const result: Record<string, unknown> = sanitizeOrderDetail(order);
      if (includeMovements) {
        result.movements = (movements ?? []).map(sanitizeCajaMovement);
      }
      return result;
    },
  },
];
